import { create } from 'zustand';
import { ListFamilies, CreateFamily, GetFamily, UpdateFamily, DeleteFamily } from '../api/api';
import toast from 'react-hot-toast';

export interface Family {
  _id: string;
  familyName: string;
  unit: string | { _id: string; name: string; unitNumber: string };
  guardian?: string | { _id: string; name: string };
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

interface CreateFamilyI {
  familyName: string;
  unit: string;
  guardian?: any;
  members?: any[];
}

interface UpdateFamilyI {
  familyName?: string;
  unit?: string;
  guardian?: string;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

interface GetFamiliesResponse {
  data: {
    families: Family[];
    pagination?: PaginationInfo;
  };
}

interface FamilyResponse {
  data: {
    family: Family;
    members?: any[];
  };
  status: boolean;
  message: string;
}

interface DeleteFamilyResponse {
  status: boolean;
  message: string;
}

interface FamilyStore {
  families: Family[];
  loading: boolean;
  error: string | null;
  pagination: PaginationInfo | null;
  getFamilies: (search?: string, page?: number, limit?: number) => Promise<void>;
  createFamily: (family: CreateFamilyI) => Promise<void>;
  getFamily: (id: string) => Promise<Family | null>;
  updateFamily: (id: string, family: UpdateFamilyI) => Promise<void>;
  deleteFamily: (id: string) => Promise<void>;
}

export const useFamilyStore = create<FamilyStore>((set, get) => ({
  families: [] as Family[],
  loading: false,
  error: null,
  pagination: null,

  getFamilies: async (search?: string, page?: number, limit?: number) => {
    set({ loading: true, error: null });
    const { data, error } = await ListFamilies<GetFamiliesResponse>(search, page, limit);

    if (error) {
      set({ loading: false, error: error as string });
      return;
    }

    set({
      families: data.data.families || [],
      pagination: data.data.pagination || null,
      loading: false,
    });
  },

  createFamily: async (family: CreateFamilyI) => {
    set({ loading: true, error: null });
    const { data, error } = await CreateFamily<FamilyResponse>(family);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to create family' });
      return;
    }

    const families = get().families;
    set({ families: [data.data.family, ...families], loading: false });
    toast.success('Family created successfully');
    // Refresh the list with current pagination
    const currentPagination = get().pagination;
    get().getFamilies(undefined, currentPagination?.page, currentPagination?.limit);
  },

  getFamily: async (id: string) => {
    set({ loading: true, error: null });
    const { data, error } = await GetFamily<FamilyResponse>(id);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to get family' });
      return null;
    }

    set({ loading: false });
    return data.data.family;
  },

  updateFamily: async (id: string, family: UpdateFamilyI) => {
    set({ loading: true, error: null });
    const { data, error } = await UpdateFamily<FamilyResponse>(id, family);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to update family' });
      return;
    }

    const families = get().families;
    const updatedFamilies = families.map((f) => (f._id === id ? { ...f, ...data.data.family } : f));
    set({ families: updatedFamilies, loading: false });
    toast.success('Family updated successfully');
    // Refresh the list with current pagination
    const currentPagination = get().pagination;
    get().getFamilies(undefined, currentPagination?.page, currentPagination?.limit);
  },

  deleteFamily: async (id: string) => {
    set({ loading: true, error: null });
    const { data, error } = await DeleteFamily<DeleteFamilyResponse>(id);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to delete family' });
      return;
    }

    const families = get().families.filter((family) => family._id !== id);
    set({ families, loading: false });
    toast.success('Family deleted successfully');
    // Refresh the list with current pagination
    const currentPagination = get().pagination;
    get().getFamilies(undefined, currentPagination?.page, currentPagination?.limit);
  },
}));

export type { CreateFamilyI, UpdateFamilyI };
